import React, { useState, useEffect } from 'react';
import { Plus, Loader, AlertCircle } from 'lucide-react';

interface RecipeFormProps {
  onSubmit: (ingredients: string[]) => void;
  isLoading: boolean;
}

function RecipeForm({ onSubmit, isLoading }: RecipeFormProps) {
  const [ingredientInput, setIngredientInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [lastInteraction, setLastInteraction] = useState<number>(Date.now());

  useEffect(() => {
    const trackInteraction = () => {
      setLastInteraction(Date.now());
    };

    window.addEventListener('mousemove', trackInteraction);
    window.addEventListener('keydown', trackInteraction);
    window.addEventListener('scroll', trackInteraction);

    return () => {
      window.removeEventListener('mousemove', trackInteraction);
      window.removeEventListener('keydown', trackInteraction);
      window.removeEventListener('scroll', trackInteraction);
    };
  }, []);

  const parseIngredients = (input: string): string[] => {
    const cleanInput = input.split('for').shift() || input;
    
    return cleanInput
      .split(',')
      .flatMap(item => {
        const cleaned = item
          .replace(/\band\b/g, ',')
          .replace(/\&/g, ',')
          .replace(/\bfor\b/g, ',')
          .replace(/\ba\b/g, ',');
        
        return cleaned.split(',');
      })
      .map(ing => ing.trim().toLowerCase())
      .filter(ing => 
        ing.length > 0 && 
        !['and', '&', 'for', 'a'].includes(ing) &&
        !ing.match(/^[.,\/#!$%\^&\*;:{}=\-_`~()]+$/)
      );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const ingredients = parseIngredients(ingredientInput);
    
    if (ingredients.length === 0) {
      setError('Please enter at least one ingredient');
      return;
    }

    // More lenient timing check
    const timeSinceLastInteraction = Date.now() - lastInteraction;
    if (timeSinceLastInteraction < 50) { // Reduced from 500ms
      setError('Please try again');
      return;
    }
    
    setError(null);
    onSubmit(ingredients);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <textarea
          value={ingredientInput}
          onChange={(e) => {
            setIngredientInput(e.target.value);
            setError(null);
          }}
          placeholder="Enter ingredients (separated by commas)..."
          className="w-full p-3 min-h-[100px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-y font-body"
          disabled={isLoading}
        />
        <p className="text-sm text-text-light font-body">
          Tip: Separate ingredients with commas (e.g., butter, flour, sugar)
        </p>
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg font-body">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      
      <button
        type="submit"
        disabled={isLoading || !ingredientInput.trim()}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-body"
      >
        {isLoading ? (
          <>
            <Loader className="w-4 h-4 animate-spin" />
            Generating Recipe...
          </>
        ) : (
          'Generate Recipe'
        )}
      </button>
    </form>
  );
}

export default RecipeForm;